import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/**
 * Confirmation dialog with a free-text comment (optional or required).
 * Used for approval decisions (approve / reject / cancel / return to draft) and "lost reason" on opportunities.
 */
export const CommentDialog = ({ open, onOpenChange, title, description, label, placeholder, required = false, confirmLabel, destructive = false, loading = false, onConfirm, children, testId = "comment-dialog" }) => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [touched, setTouched] = useState(false);
  useEffect(() => { if (open) { setText(""); setTouched(false); } }, [open]);
  const invalid = required && !text.trim();
  const submit = (e) => {
    e?.preventDefault?.();
    setTouched(true);
    if (invalid) return;
    onConfirm?.(text.trim() || null);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card sm:max-w-md" data-testid={testId}>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="font-heading">{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          {children}
          <div className="space-y-1.5">
            <Label htmlFor={`${testId}-text`} className="text-sm font-medium">{label || t("common.comment")} {!required && <span className="text-xs font-normal text-muted-foreground">({t("common.optional")})</span>}</Label>
            <Textarea id={`${testId}-text`} rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} aria-invalid={touched && invalid} className={cn("bg-card", touched && invalid && "border-destructive focus-visible:ring-destructive")} data-testid={`${testId}-text`} autoFocus />
            {touched && invalid && <p className="text-xs text-destructive" role="alert" data-testid={`${testId}-error`}>{t("common.commentRequired")}</p>}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" className="bg-card" onClick={() => onOpenChange(false)} data-testid={`${testId}-cancel`}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={loading} className={cn(destructive && "bg-destructive text-destructive-foreground hover:bg-destructive/90")} data-testid={`${testId}-confirm`}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{confirmLabel || t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
